import WasmController from "react-lib/frameworks/WasmController";
// api
import PatientRecordInterfaceUpdateView from "issara-sdk/apis/PatientRecordInterfaceUpdateView_apps_INF";
import PatientRecordInterfaceList from "issara-sdk/apis/PatientRecordInterfaceList_apps_INF"

import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";
import moment from "moment";

// Serializer
import PatientRecordInterfaceSerializer from "issara-sdk/types/PatientRecordInterfaceSerializer_apps_INF";
import UserList from "issara-sdk/apis/UserList_users";

let toDate = moment();

export type Props = {

}

export type State = Partial<{
  // Sequence
  PatientRecordInterfaceSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    searchPatientRecord: any;
    patientRecordList: any;
    userOptions: any;
    paginatedItems: { items?: any[]; page?: number; pageSize?: number; total?: number };
  }>;

  buttonLoadCheck?: any;
  errorMessage?: any;
  successMessage?: any;
}>;

export const StateInitial: State = {
  // Sequence
  PatientRecordInterfaceSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  errorMessage: null,
  successMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

export type AccessorType = Partial<Record<keyof PatientRecordInterfaceSerializer, string>>;

export type WidthsType = Partial<Record<keyof PatientRecordInterfaceSerializer, number>>;

export const LIST_LIMIT = 20;

const PAGINATED_INIT = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

type Handler = (controller: WasmController<State, Event, Data, Props>, params?: any) => any;
/* ------------------------------------------------------ */
/*                          START                         */
/* ------------------------------------------------------ */
export const Start: Handler = async (controller, params) => {
  let state = controller.getState();

  controller.setState(
    {
      PatientRecordInterfaceSequence: {
        ...state.PatientRecordInterfaceSequence,
        sequenceIndex: "Action",
        searchPatientRecord: {
          createDate: dateToStringWithoutTimeBE(toDate),
        },
        paginatedItems: { pageSize: LIST_LIMIT, page: 0 },
      },
    },
    // () => Action(controller, { action: "search" })
  );
};

/* ------------------------------------------------------ */
/*                         ACTION                         */
/* ------------------------------------------------------ */
export const Action: Handler = async (controller, params) => {
  if (params.action === "search") {
    HandleSearchData(controller, params);
  } else if (params.action === "clear") {
    HandleClearData(controller, params);
  } else if (params.action === "save") {
    HandleSaveData(controller, params);
  }
};

const HandleSearchData: Handler = async (controller, params) => {
  const state = controller.getState();
  const searchPatientRecord = state.PatientRecordInterfaceSequence?.searchPatientRecord;
  const paginatedItems = state.PatientRecordInterfaceSequence?.paginatedItems;

  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${params.cardKey}_search`]: "LOADING",
    },
  });

  const offset = page * pageSize;

  let data = {
    limit: pageSize,
    offset,
    create_date: searchPatientRecord?.createDate,
    hn: searchPatientRecord?.hn,
    full_name: searchPatientRecord?.fullName,
  };

  const [patientRecordListRes, patientRecordListErr, patientRecordListNet] = await PatientRecordInterfaceList.list({
    apiToken: controller.apiToken,
    params: data,
  });

  if (patientRecordListRes) {
    const uniqueData = (patientRecordListRes?.items || [])
      .filter(
        (item: any, index: number, self: any) =>
          index === self.findIndex((t: any) => t.created_by === item.created_by)
      )
      .map((item: any) => item?.created_by);

    const userOptions = await Promise.all(
      uniqueData.map(async (id: number) => {
        const [userRes, userErr, userNet] = await UserList.list({
          apiToken: controller.apiToken,
          params: { pk: id }
        });

        return userRes?.items?.[0];
      })
    );

    controller.setState({
      PatientRecordInterfaceSequence: {
        ...state.PatientRecordInterfaceSequence,
        patientRecordList: patientRecordListRes?.items,
        userOptions: userOptions,
        paginatedItems: { 
          ...paginatedItems,
          page,
          pageSize,
          total: patientRecordListRes?.total
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.cardKey}_search`]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.cardKey}_search`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [params.cardKey]: patientRecordListErr,
      }
    });
  }
};

const HandleClearData: Handler = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    PatientRecordInterfaceSequence: {
      ...state.PatientRecordInterfaceSequence,
      searchPatientRecord: {
        createDate: "",
        hn: "",
        fullName: "",
      },
      patientRecordList: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    },
  });
};

const HandleSaveData: Handler = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${params.cardKey}_${params.action}`]: "LOADING",
    },
  });

  const apiType =
    params.type === "add"
      ? PatientRecordInterfaceUpdateView.post
      : PatientRecordInterfaceUpdateView.put;

  const [billingPYRes, billingPYErr, billingPYNet] = await apiType({
    apiToken: controller.apiToken,
    data: {
      item_id_list: params?.selectedItem || [],
    },
  });

  if (billingPYRes) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.cardKey}_${params.type}`]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [params.cardKey]: "The system has successfully",
      },
    });

    HandleSearchData(controller, params);
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.cardKey}_${params.type}`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [params.cardKey]:  billingPYErr,
      },
    });
  }
};

/* ------------------------------------------------------ */
/*                         UTILS                          */
/* ------------------------------------------------------ */

export const accessorList: Partial<Record<keyof PatientRecordInterfaceSerializer, string>> = {
  id: "ID",
  valid: "Valid",
  error_message: "Error Message",
  created_by: "User ที่สร้าง",
  created_at: "วันเวลาที่สร้าง",
  sent: "Sent",
  file_name: "File Name",
  version: "Version",
  company_code: "Company Code",
  debtor_group: "Debtor Group",
  hn: "HN",
  full_name: "Full Name",
  father: "Father",
  mother: "Mother",
  birthdate: "Birthdate",
  gender: "Gender",
  marriage: "Marriage",
  career: "Career",
  home_address: "Home Address",
  district: "District",
  province: "Province",
  zipcode: "Zipcode",
  country: "Country",
  citizen_no: "Citizen No",
  personal_no: "Personal No",
  prev_acct_no: "Prev Acct No",
  payment_term: "Payment Term",
  status: "Status",
};

export const GenerateTableHeader = (
  columnnWidths?: WidthsType
): TableHeaderType<PatientRecordInterfaceSerializer>[] => {
  return (Object.entries(accessorList) as [keyof PatientRecordInterfaceSerializer, string][]).map(
    ([key, label]) => ({
      Header: label,
      accessor: key,
      ...(columnnWidths && columnnWidths[key] ? { width: columnnWidths[key] } : {}),
    })
  );
};